import ChatIcon from '@mui/icons-material/Chat';
import DownloadIcon from '@mui/icons-material/Download';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import ReplayIcon from '@mui/icons-material/Replay';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { LineChart } from '@mui/x-charts/LineChart';
// PieChart intentionally not imported — design system §6 forbids pies with > 3 segments.
// Categorical breakdowns use HorizontalBarBreakdown below.
import PillTabs from 'components/common/PillTabs';
import moment from 'moment';
import TemplateMessageModal from 'pages/Client/TemplateMessageModal';
import React, { type FC, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetClientQuery } from 'services/api/clients.api';
import type {
  BusinessUnitLeadMetrics,
  BusinessUnitResponseTimesMetrics,
  TemplateClientDetail,
} from 'services/types/metrics';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import buildLeadsFilterUrl from 'utils/buildLeadsFilterUrl';
import { INSUREX_BUSINESS_UNIT_ID, KOVACS_BUSINESS_UNIT_ID } from 'utils/constants';

import ChartCard from './ChartCard';
import HardMetricCard from './HardMetricCard';

/** Backend `/insights/response-times` failed-template query off (DB cost). Set `true` when restored. */
const FAILED_TEMPLATE_METRICS_ENABLED = false;

const minutesToString = (minutes: number | string) => {
  if (minutes === 'noResponse') {
    return 'Sin respuesta';
  }
  if (minutes === 'failed') {
    return 'Fallidos';
  }
  const mins = Number(minutes);
  if (Number.isNaN(mins)) {
    return String(minutes);
  }
  if (mins < 60) {
    return `<${mins}m`;
  }
  if (mins < 1440) {
    return `<${Math.floor(mins / 60)}h`;
  }
  return `<${Math.floor(mins / 1440)}d`;
};

const FAILED_BAR_COLOR = '#d32f2f';
const NO_RESPONSE_BAR_COLOR = '#9e9e9e';

const sortResponseTimeKeys = (keys: (string | number)[]): (string | number)[] =>
  [...keys].sort((a, b) => {
    if (a === 'failed') return 1;
    if (b === 'failed') return -1;
    if (a === 'noResponse') return 1;
    if (b === 'noResponse') return -1;
    return Number(a) - Number(b);
  });

const buildResponseTimeChartSeries = (
  data: Record<string | number, number>,
  showNoResponse: boolean,
  showFailed: boolean
): {
  keys: (string | number)[];
  series: {
    data: number[];
    label?: string;
    stack?: string;
    color?: string;
    valueFormatter?: (v: number | null) => string;
  }[];
} => {
  const rawKeys = Object.keys(data).filter(
    (key) => (showNoResponse || key !== 'noResponse') && (showFailed || key !== 'failed')
  ) as (string | number)[];
  const keys = sortResponseTimeKeys(rawKeys);
  const total: number = keys.reduce((acc: number, key): number => acc + (Number(data[key]) || 0), 0);
  const valueFormatter = (value: number | null): string => {
    const num = value ?? 0;
    const pct: string = total ? ((num * 100) / total).toFixed(1) : '0';
    return `${num} (${pct}%)`;
  };
  const hasNoResponse = keys.includes('noResponse');
  const hasFailed = keys.includes('failed');
  if (hasNoResponse || hasFailed) {
    const series: {
      data: number[];
      label?: string;
      stack?: string;
      color?: string;
      valueFormatter?: (v: number | null) => string;
    }[] = [
      {
        data: keys.map((k) => (k === 'noResponse' || k === 'failed' ? 0 : (data[k] ?? 0))),
        label: 'Respondieron',
        stack: 'stack1',
        valueFormatter,
      },
    ];
    if (hasNoResponse) {
      series.push({
        data: keys.map((k) => (k === 'noResponse' ? (data[k] ?? 0) : 0)),
        label: 'Sin respuesta',
        stack: 'stack1',
        color: NO_RESPONSE_BAR_COLOR,
        valueFormatter,
      });
    }
    if (hasFailed) {
      series.push({
        data: keys.map((k) => (k === 'failed' ? (data[k] ?? 0) : 0)),
        label: 'Fallidos',
        stack: 'stack1',
        color: FAILED_BAR_COLOR,
        valueFormatter,
      });
    }
    return { keys, series };
  }
  return {
    keys,
    series: [
      {
        data: keys.map((k) => data[k] ?? 0),
        label: 'Respondieron',
        valueFormatter,
      },
    ],
  };
};

interface BreakdownItem {
  id: number;
  label: string;
  value: number;
}

/**
 * Renders the indented subcategory bars shown under a parent category row when
 * expanded. Widths and shares are relative to the parent category's own total.
 */
const SubOriginBars: FC<{ items: BreakdownItem[]; parentValue: number }> = ({ items, parentValue }) => {
  const sorted = [...items].sort((a, b) => b.value - a.value);
  const denominator = parentValue || 1;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1, pl: 2 }}>
      {sorted.map((item) => {
        const widthPct = Math.min((item.value / denominator) * 100, 100);
        const sharePct = (item.value / denominator) * 100;
        return (
          <Box key={`sub-${item.id}-${item.label}`} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  color: alpha(NAVY, 0.72),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  pr: 1,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: alpha(NAVY, 0.72),
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {item.value.toLocaleString()}{' '}
                <Typography component="span" sx={{ color: alpha(NAVY, 0.4), fontWeight: 500 }}>
                  ({sharePct.toFixed(1)}%)
                </Typography>
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'relative',
                height: 6,
                borderRadius: 999,
                backgroundColor: alpha(NAVY, 0.04),
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${widthPct}%`,
                  borderRadius: 999,
                  backgroundColor: alpha(NAVY, 0.32),
                  transition: 'width 0.46s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Direct-labeled horizontal bar breakdown — replaces pie charts with > 3 segments.
 *
 * When `subItemsByLabel` + `expanded` are provided, each parent row can expand
 * to show its subcategory bars indented beneath it.
 */
const HorizontalBarBreakdown: FC<{
  data: BreakdownItem[];
  max?: number;
  subItemsByLabel?: Record<string, BreakdownItem[]>;
  expanded?: boolean;
}> = ({ data, max = 12, subItemsByLabel, expanded = false }) => {
  if (!data.length) {
    return (
      <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.5), fontSize: '0.875rem' }}>
        Sin datos en el período seleccionado.
      </Typography>
    );
  }
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const visible = sorted.slice(0, max);
  const rest = sorted.slice(max);
  const restTotal = rest.reduce((acc, r) => acc + r.value, 0);
  const items = restTotal > 0 ? [...visible, { id: -1, label: `Otros (${rest.length})`, value: restTotal }] : visible;
  const total = sorted.reduce((acc, item) => acc + item.value, 0);
  const scaleMax = items[0]?.value || 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {items.map((item) => {
        const widthPct = (item.value / scaleMax) * 100;
        const sharePct = total > 0 ? (item.value / total) * 100 : 0;
        const subItems = subItemsByLabel?.[item.label] ?? [];
        return (
          <Box key={`${item.id}-${item.label}`} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: NAVY,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  pr: 1,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: NAVY,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {item.value.toLocaleString()}{' '}
                <Typography component="span" sx={{ color: alpha(NAVY, 0.5), fontWeight: 500 }}>
                  ({sharePct.toFixed(1)}%)
                </Typography>
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'relative',
                height: 8,
                borderRadius: 999,
                backgroundColor: alpha(NAVY, 0.05),
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${widthPct}%`,
                  borderRadius: 999,
                  backgroundColor: alpha(NAVY, 0.55),
                  transition: 'width 0.46s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              />
            </Box>
            {subItemsByLabel && subItems.length > 0 && (
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <SubOriginBars items={subItems} parentValue={item.value} />
              </Collapse>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

interface LeadChartsFilters {
  startDate: Date;
  endDate: Date;
  businessUnitId: number;
  sources?: string[];
  brands?: string[];
  campaignIds?: string[];
}

/**
 * Custom tooltip for the "Leads contactados vs. subidos al CRM" trend chart.
 * Replaces MUI's default tooltip with a cleaner layout: a localized Spanish
 * date header, a colored marker per series, and (for "En CRM") a percentage
 * of the same-day "Contactados" value.
 */
const stripParenSuffix = (label: string) => label.replace(/\s*\(.*\)\s*$/, '');

const TrendTooltipContent: FC = () => {
  const axesData = useAxesTooltip();
  if (!axesData || axesData.length === 0) return null;
  const axis = axesData[0];
  const rawDate = String(axis.axisValue);
  const m = moment(rawDate, 'YYYY-MM-DD', true);
  const dateLabel = m.isValid() ? m.locale('es').format('dddd D [de] MMMM, YYYY') : rawDate;
  const capitalizedDate = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);

  const contactedItem = axis.seriesItems.find((s) => /contactad/i.test(String(s.formattedLabel ?? '')));
  const contactedValue = typeof contactedItem?.value === 'number' ? contactedItem.value : 0;

  return (
    <Box
      sx={{
        fontFamily: 'Jost, sans-serif',
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_CARD,
        boxShadow: SHADOW,
        py: 1.25,
        px: 1.75,
        minWidth: 220,
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 700,
          fontSize: '0.8rem',
          color: NAVY,
          mb: 0.75,
        }}
      >
        {capitalizedDate}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {axis.seriesItems.map((item) => {
          const value = typeof item.value === 'number' ? item.value : 0;
          const label = stripParenSuffix(String(item.formattedLabel ?? ''));
          const isCRM = /crm/i.test(label);
          const pct = isCRM && contactedValue > 0 ? `${((value * 100) / contactedValue).toFixed(1)}%` : null;
          return (
            <Box key={String(item.seriesId)} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.8rem',
                  color: `${NAVY}cc`,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  color: NAVY,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {value.toLocaleString()}
              </Typography>
              {pct && (
                <Typography
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.75rem',
                    color: `${NAVY}80`,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 48,
                    textAlign: 'right',
                  }}
                >
                  {pct}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const TrendTooltip: FC<React.HTMLAttributes<HTMLDivElement>> = (props) => (
  <ChartsTooltipContainer {...props} trigger="axis">
    <TrendTooltipContent />
  </ChartsTooltipContainer>
);

/**
 * Generic axis tooltip for categorical bar charts (hours, response-time buckets, etc.).
 * Same visual language as `TrendTooltip`: header is the axis label, rows show a colored
 * dot, the series label, the value, and (when the series' `valueFormatter` returns a
 * "<value> (<pct>%)" string) a separate right-aligned percentage column for alignment.
 */
const parseValuePct = (s: string): { value: string; pct: string | null } => {
  const m = s.match(/^(.*?)\s*\(([\d.]+%)\)\s*$/);
  if (m) return { value: m[1].trim(), pct: m[2] };
  return { value: s.trim(), pct: null };
};

const CategoryTooltipContent: FC = () => {
  const axesData = useAxesTooltip();
  if (!axesData || axesData.length === 0) return null;
  const axis = axesData[0];
  const header = String(axis.axisFormattedValue ?? axis.axisValue ?? '');
  const visibleItems = axis.seriesItems.filter((s) => {
    const raw = typeof s.value === 'number' ? s.value : 0;
    return raw > 0;
  });
  if (visibleItems.length === 0) return null;
  return (
    <Box
      sx={{
        fontFamily: 'Jost, sans-serif',
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_CARD,
        boxShadow: SHADOW,
        py: 1.25,
        px: 1.75,
        minWidth: 200,
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 700,
          fontSize: '0.8rem',
          color: NAVY,
          mb: 0.75,
        }}
      >
        {header}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {visibleItems.map((item) => {
          const rawValue = typeof item.value === 'number' ? item.value : 0;
          const formatted = String(item.formattedValue ?? rawValue.toLocaleString());
          const { value, pct } = parseValuePct(formatted);
          const label = stripParenSuffix(String(item.formattedLabel ?? '')) || 'Total';
          return (
            <Box key={String(item.seriesId)} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.8rem',
                  color: `${NAVY}cc`,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  color: NAVY,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {value}
              </Typography>
              {pct && (
                <Typography
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.75rem',
                    color: `${NAVY}80`,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 48,
                    textAlign: 'right',
                  }}
                >
                  {pct}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const CategoryTooltip: FC<React.HTMLAttributes<HTMLDivElement>> = (props) => (
  <ChartsTooltipContainer {...props} trigger="axis">
    <CategoryTooltipContent />
  </ChartsTooltipContainer>
);

interface LeadChartProps {
  leadMetrics?: BusinessUnitLeadMetrics;
  responseTimesMetrics?: BusinessUnitResponseTimesMetrics;
  businessUnitId?: number | null;
  businessUnitType?: string;
  onNavigateToLeads?: (url: string) => void;
  filters?: LeadChartsFilters;
}

/**
 * Lead charts component for displaying various lead metrics visualizations
 *
 * @param {Object} props - The props object
 * @param {BusinessUnitLeadMetrics} [props.leadMetrics] - Lead metrics data
 * @param {BusinessUnitResponseTimesMetrics} [props.responseTimesMetrics] - Response times metrics data
 * @param {number | null} [props.businessUnitId] - Business unit ID to filter charts
 * @returns {React.ReactElement} Lead charts component
 */
const LeadCharts: FC<LeadChartProps> = ({
  leadMetrics,
  responseTimesMetrics,
  businessUnitId,
  businessUnitType,
  onNavigateToLeads,
  filters,
}: LeadChartProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showNoResponse, setShowNoResponse] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [showByTemplate, setShowByTemplate] = useState(false);
  const [originBreakdownMode, setOriginBreakdownMode] = useState<'origin' | 'source'>('origin');
  const [interestBreakdownMode, setInterestBreakdownMode] = useState<'brand' | 'model'>('brand');
  const [showSubOrigins, setShowSubOrigins] = useState(false);
  const [templateClientsDialog, setTemplateClientsDialog] = useState<{
    templateName: string;
    responded: TemplateClientDetail[];
    noResponse: TemplateClientDetail[];
    failed: TemplateClientDetail[];
  } | null>(null);
  const [resendClientTarget, setResendClientTarget] = useState<{ clientId: number; templateName: string } | null>(null);

  const { data: resendClient, isFetching: isFetchingResendClient } = useGetClientQuery(
    String(resendClientTarget?.clientId ?? ''),
    {
      skip: !resendClientTarget,
    }
  );

  if (!leadMetrics) {
    return <CircularProgress />;
  }

  const sourceChartData = Object.entries(leadMetrics.chartsData.leadsBySource)
    .map((source, index) => ({
      id: index,
      value: source[1],
      label: source[0].length > 43 ? `${source[0].slice(0, 20)}...${source[0].slice(-20)}` : source[0],
    }))
    .sort((a, b) => b.value - a.value);

  const originChartData = Object.entries(leadMetrics.chartsData.leadsByOrigin ?? {})
    .map(([label, value], index) => ({ id: index, value, label }))
    .sort((a, b) => b.value - a.value);

  const originSubItemsByLabel: Record<string, BreakdownItem[]> = Object.fromEntries(
    Object.entries(leadMetrics.chartsData.leadsByOriginSubOrigin ?? {}).map(([origin, subOrigins]) => [
      origin,
      Object.entries(subOrigins).map(([label, value], index) => ({ id: index, value, label })),
    ])
  );
  const hasSubOrigins = Object.values(originSubItemsByLabel).some((items) => items.length > 0);

  const toBreakdown = (counts: Record<string, number>) =>
    Object.entries(counts)
      .map(([label, value], index) => ({
        id: index,
        value,
        label: label.length > 43 ? `${label.slice(0, 20)}...${label.slice(-20)}` : label,
      }))
      .sort((a, b) => b.value - a.value);

  const brandChartData = toBreakdown(leadMetrics.chartsData.leadsByBrand ?? {});
  const modelChartData = toBreakdown(leadMetrics.chartsData.leadsByModel ?? {});

  // Mueve Seguro only — sales breakdowns. Both records come back as `undefined`
  // for any other BU, in which case these arrays end up empty and the cards self-hide.
  const salesByTypeChartData = Object.entries(leadMetrics.chartsData.salesByType ?? {})
    .map(([label, value], index) => ({ id: index, value, label }))
    .sort((a, b) => b.value - a.value);

  const salesByInstitutionChartData = Object.entries(leadMetrics.chartsData.salesByInstitution ?? {})
    .map(([rawLabel, value], index) => ({
      id: index,
      value,
      label: rawLabel.length > 43 ? `${rawLabel.slice(0, 20)}...${rawLabel.slice(-20)}` : rawLabel,
    }))
    .sort((a, b) => b.value - a.value);

  const statusLabelMap = [
    {
      label: 'CREATED',
      value: 'Creado',
    },
    {
      label: 'HANDLED_BY_AI',
      value: 'Atendido por AI',
    },
    {
      label: 'PENDING_TO_BE_HANDLED',
      value: 'Pendiente de atender',
    },
    {
      label: 'PENDING_TO_BE_ASSIGNED',
      value: 'Por asignar',
    },
    {
      label: 'ASSIGNED_TO_SALESMAN',
      value: 'Asignado a ejecutivo',
    },
    {
      label: 'HANDLED_BY_SALESMAN',
      value: 'Atendido por ejecutivo',
    },
    {
      label: 'CLOSED',
      value: 'Cerrado',
    },
  ];

  const mapLabel = (label: string) => {
    const status = statusLabelMap.find((item) => item.label === label);
    return status ? status.value : label;
  };

  const leadsByAIComment = leadMetrics.chartsData.leadsByAIComment ?? {};
  const kovacsAICommentChartData = Object.entries(leadsByAIComment).map(([label, value], index) => ({
    id: index,
    value,
    label: label.length > 50 ? `${label.slice(0, 47)}...` : label,
  }));

  const statusChartData = Object.entries(leadMetrics.chartsData.leadsByStatus)
    .map((status, index) => ({
      id: index,
      value: status[1],
      label: mapLabel(status[0]),
      originalKey: status[0],
    }))
    .sort((a, b) => {
      const aIndex = statusLabelMap.findIndex((item) => item.value === a.label);
      const bIndex = statusLabelMap.findIndex((item) => item.value === b.label);
      return aIndex - bIndex;
    })
    .map((item, index) => ({
      id: index,
      value: item.value,
      label: item.label,
    }));

  const topicsChartData = Object.entries(leadMetrics.chartsData.leadsByTopic)
    .map(([topic, value], index) => ({
      id: index,
      value,
      label: topic.length > 43 ? `${topic.slice(0, 20)}...${topic.slice(-20)}` : topic,
    }))
    .sort((a, b) => b.value - a.value);

  const tagsChartData = Object.entries(leadMetrics.chartsData.leadsByTag ?? {})
    .map(([tag, value], index) => ({
      id: index,
      value,
      label: tag.length > 43 ? `${tag.slice(0, 20)}...${tag.slice(-20)}` : tag,
    }))
    .sort((a, b) => b.value - a.value);

  const clientsByResponseTime = { ...(responseTimesMetrics?.chartsData.clientsByResponseTime || {}) };
  delete clientsByResponseTime[0];
  if (!FAILED_TEMPLATE_METRICS_ENABLED) {
    delete clientsByResponseTime.failed;
  }

  const effectiveShowFailed = FAILED_TEMPLATE_METRICS_ENABLED && showFailed;

  const responseTimeChart = buildResponseTimeChartSeries(clientsByResponseTime, showNoResponse, effectiveShowFailed);

  const totalTemplateMessages: number = responseTimesMetrics?.hardMetrics?.totalTemplateMessages ?? 0;
  const clientsByResponseTimeByTemplate: Record<string, Record<number | string, number>> = responseTimesMetrics
    ?.chartsData?.clientsByResponseTimeByTemplate ?? {};
  type TemplateDetailsByTemplate = Record<
    string,
    { responded: TemplateClientDetail[]; noResponse: TemplateClientDetail[]; failed: TemplateClientDetail[] }
  >;
  const clientsByResponseTimeByTemplateDetails: TemplateDetailsByTemplate =
    responseTimesMetrics?.chartsData?.clientsByResponseTimeByTemplateDetails ?? {};

  const handleOpenTemplateClientsDialog = (templateName: string) => {
    const details = clientsByResponseTimeByTemplateDetails[templateName];
    if (!details) {
      return;
    }
    setTemplateClientsDialog({
      templateName,
      responded: details.responded,
      noResponse: details.noResponse,
      failed: details.failed ?? [],
    });
  };

  const handleCloseTemplateClientsDialog = () => {
    setTemplateClientsDialog(null);
  };

  const handleNavigateToClient = (clientId: number) => {
    navigate(`/clients/${clientId}`, { state: { from: location.pathname + location.search } });
    handleCloseTemplateClientsDialog();
  };

  const handleOpenResendTemplateModal = (client: TemplateClientDetail) => {
    if (!templateClientsDialog) {
      return;
    }

    setResendClientTarget({
      clientId: client.clientId,
      templateName: templateClientsDialog.templateName,
    });
  };

  const handleCloseResendTemplateModal = () => {
    setResendClientTarget(null);
  };

  const handleDownloadCSV = () => {
    if (!templateClientsDialog) return;

    const { templateName, responded, noResponse, failed } = templateClientsDialog;

    // Combine all lists with response status
    const allClients = [
      ...responded.map((client) => ({ ...client, responseStatus: 'Respondió' })),
      ...noResponse.map((client) => ({ ...client, responseStatus: 'Sin respuesta' })),
      ...(FAILED_TEMPLATE_METRICS_ENABLED ? (failed ?? []) : []).map((client) => ({
        ...client,
        responseStatus: 'Falló envío plantilla',
      })),
    ];

    // Create CSV headers
    const headers = [
      'ID Cliente',
      'Teléfono',
      'Nombre',
      'Apellido',
      'Email',
      'Fuente',
      'Plantilla Enviada',
      'Estado de Respuesta',
      'Tiempo de Respuesta (min)',
    ];

    // Create CSV rows
    const rows = allClients.map((client) => {
      const responseTimeMinutes =
        client.responseTimeInSeconds !== null ? Math.round(client.responseTimeInSeconds / 60) : '-';

      return [
        client.clientId,
        client.phone || '-',
        client.firstName || '-',
        client.lastName || '-',
        client.email || '-',
        client.source || '-',
        new Date(client.templateSentAt).toLocaleString('es-CL'),
        client.responseStatus,
        responseTimeMinutes,
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `template_${templateName}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderClientList = (clients: TemplateClientDetail[], options?: { allowResend?: boolean }) => {
    if (!clients.length) {
      return (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Sin resultados
          </Typography>
        </Box>
      );
    }
    return (
      <List
        dense
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          maxHeight: 330,
          overflowY: 'auto',
        }}
      >
        {clients.map((client, index) => {
          const fullName = [client.firstName, client.lastName].filter(Boolean).join(' ') || 'Sin nombre';
          const isResendLoading = resendClientTarget?.clientId === client.clientId && isFetchingResendClient;

          return (
            <React.Fragment key={`${client.clientId}-${client.phone ?? 'no-phone'}`}>
              {index > 0 && <Divider variant="inset" component="li" />}
              <ListItem
                disablePadding
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {options?.allowResend && (
                      <Tooltip title="Reenviar plantilla">
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            disabled={isResendLoading}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenResendTemplateModal(client);
                            }}
                          >
                            {isResendLoading ? <CircularProgress size={18} /> : <ReplayIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Ir al chat">
                      <IconButton edge="end" size="small" onClick={() => handleNavigateToClient(client.clientId)}>
                        <ChatIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemButton onClick={() => handleNavigateToClient(client.clientId)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <PersonIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={`ID: ${client.clientId}`} size="small" variant="outlined" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {fullName}
                        </Typography>
                        {client.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                              }}
                            >
                              {client.phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                    secondary={
                      client.email && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {client.email}
                        </Typography>
                      )
                    }
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Box>
      <Grid container sx={{ flexDirection: 'column' }}>
        <ChartCard
          eyebrow="Origen"
          title="De dónde llegan los leads"
          description={
            originBreakdownMode === 'origin'
              ? 'Top orígenes categorizados, ordenados por volumen contactado.'
              : 'Top fuentes (source) tal como llegan, ordenadas por volumen contactado.'
          }
        >
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <PillTabs
              tabs={[
                { value: 'origin', label: 'Categoría' },
                { value: 'source', label: 'Origen' },
              ]}
              value={originBreakdownMode}
              onChange={setOriginBreakdownMode}
              ariaLabel="Tipo de desglose de origen"
            />
            {originBreakdownMode === 'origin' && hasSubOrigins && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showSubOrigins}
                    onChange={(e) => setShowSubOrigins(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: NAVY,
                        '&:hover': { backgroundColor: alpha(NAVY, 0.08) },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: NAVY,
                        opacity: 1,
                      },
                    }}
                  />
                }
                label="Ver subcategorías"
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: alpha(NAVY, 0.72),
                  },
                }}
              />
            )}
          </Box>
          {originBreakdownMode === 'origin' ? (
            <HorizontalBarBreakdown
              data={originChartData}
              subItemsByLabel={originSubItemsByLabel}
              expanded={showSubOrigins}
            />
          ) : (
            <HorizontalBarBreakdown data={sourceChartData} />
          )}
        </ChartCard>

        <ChartCard
          eyebrow="Tendencia diaria"
          title="Leads contactados vs. subidos al CRM"
          description="Recorrido día por día desde el contacto hasta el CRM."
        >
          {(() => {
            const dailyContacted = leadMetrics.chartsData.leadsByCreationDay;
            const dailyCRM = leadMetrics.chartsData.leadsUploadedToCRMByCreationDay;
            const dates = Object.keys(dailyContacted);
            const contactedValues = Object.values(dailyContacted);
            const crmValues = Object.values(dailyCRM);
            const totalContacted = contactedValues.reduce((a, b) => a + b, 0);
            const totalCRM = crmValues.reduce((a, b) => a + b, 0);

            // Show roughly 8 evenly-spaced labels, regardless of range.
            const targetTicks = 8;
            const step = Math.max(1, Math.ceil(dates.length / targetTicks));
            const formatTick = (raw: string) => {
              const m = moment(raw, 'YYYY-MM-DD', true);
              return m.isValid() ? m.locale('es').format('D MMM') : raw;
            };

            return (
              <LineChart
                colors={[alpha(NAVY, 0.65), YELLOW]}
                series={[
                  {
                    data: contactedValues,
                    label: `Contactados (${totalContacted})`,
                    showMark: false,
                    curve: 'monotoneX',
                    area: true,
                  },
                  {
                    data: crmValues,
                    label: `En CRM (${totalCRM})`,
                    showMark: false,
                    curve: 'monotoneX',
                  },
                ]}
                height={320}
                xAxis={[
                  {
                    scaleType: 'point',
                    data: dates,
                    valueFormatter: (raw: string, ctx) =>
                      ctx?.location === 'tick' ? formatTick(raw) : formatTick(raw),
                    tickInterval: (_value: string, index: number) => index % step === 0,
                    tickLabelStyle: {
                      fontSize: 11,
                      fill: alpha(NAVY, 0.55),
                    },
                  },
                ]}
                yAxis={[{ min: 0, tickMinStep: 1 }]}
                margin={{ top: 24, bottom: 32, left: 28, right: 16 }}
                grid={{ horizontal: true }}
                sx={{
                  [`& .MuiChartsAxis-line`]: { stroke: 'transparent' },
                  [`& .MuiChartsAxis-tick`]: { stroke: 'transparent' },
                  [`& .MuiChartsGrid-line`]: { stroke: alpha(NAVY, 0.06) },
                  [`& .MuiChartsAxis-tickLabel`]: { fontFamily: 'Jost, sans-serif' },
                  [`& .MuiAreaElement-root`]: { fillOpacity: 0.12 },
                  [`& .MuiLineElement-root`]: { strokeWidth: 2 },
                }}
                slots={{ tooltip: TrendTooltip }}
                slotProps={{
                  legend: { position: { vertical: 'top', horizontal: 'end' } },
                }}
              />
            );
          })()}
        </ChartCard>

        <ChartCard
          eyebrow="Cuándo llegan"
          title="Distribución de leads contactados por hora del día"
          description="Identifica picos para alinear capacidad y horarios de respuesta."
        >
          {(() => {
            const hourValues = Object.values(leadMetrics.chartsData.leadsByCreationHour);
            const hourTotal = hourValues.reduce((a, b) => a + b, 0);
            const hourFormatter = (v: number | null): string => {
              const num = v ?? 0;
              const pct = hourTotal > 0 ? ((num * 100) / hourTotal).toFixed(1) : '0';
              return `${num.toLocaleString()} (${pct}%)`;
            };
            return (
              <BarChart
                colors={[alpha(NAVY, 0.65)]}
                series={[
                  {
                    data: hourValues,
                    label: 'Leads contactados',
                    valueFormatter: hourFormatter,
                  },
                ]}
                height={280}
                xAxis={[
                  {
                    scaleType: 'band',
                    data: Object.keys(leadMetrics.chartsData.leadsByCreationHour).map((hour) => `${hour}h`),
                    height: 'auto' as const,
                  },
                ]}
                yAxis={[{ min: 0, tickMinStep: 1 }]}
                margin={{ top: 16, bottom: 40, left: 32, right: 16 }}
                grid={{ horizontal: true }}
                sx={{
                  [`& .MuiChartsAxis-line, & .MuiChartsAxis-tick`]: { stroke: alpha(NAVY, 0.1) },
                  [`& .MuiChartsGrid-line`]: { stroke: alpha(NAVY, 0.06) },
                  [`& .MuiChartsAxis-tickLabel`]: { fill: alpha(NAVY, 0.6), fontFamily: 'Jost, sans-serif' },
                }}
                slots={{ tooltip: CategoryTooltip }}
                hideLegend
              />
            );
          })()}
        </ChartCard>

        {businessUnitType === 'car_dealership' && brandChartData.length > 0 && (
          <ChartCard
            eyebrow={interestBreakdownMode === 'brand' ? 'Marca de interés inicial' : 'Modelo de interés inicial'}
            title={interestBreakdownMode === 'brand' ? 'Qué marcas buscan los leads' : 'Qué modelos buscan los leads'}
            description={
              interestBreakdownMode === 'brand'
                ? 'Distribución de leads por marca de interés inicial.'
                : 'Distribución de leads por modelo de interés inicial, con su marca en el nombre.'
            }
          >
            {modelChartData.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <PillTabs
                  tabs={[
                    { value: 'brand', label: 'Marca' },
                    { value: 'model', label: 'Modelo' },
                  ]}
                  value={interestBreakdownMode}
                  onChange={setInterestBreakdownMode}
                  ariaLabel="Tipo de desglose de interés"
                />
              </Box>
            )}
            <HorizontalBarBreakdown data={interestBreakdownMode === 'brand' ? brandChartData : modelChartData} />
          </ChartCard>
        )}

        <ChartCard
          eyebrow="Estado"
          title="En qué etapa están los leads contactados"
          description="Etapa actual del pipeline para los leads contactados en el período."
        >
          <HorizontalBarBreakdown data={statusChartData} />
        </ChartCard>

        {businessUnitId === INSUREX_BUSINESS_UNIT_ID && topicsChartData.length > 0 && (
          <ChartCard
            eyebrow="Tópicos"
            title="Tópicos identificados en las conversaciones"
            description="Categorías que la IA detecta en los mensajes del cliente."
          >
            <HorizontalBarBreakdown data={topicsChartData} />
          </ChartCard>
        )}

        {tagsChartData.length > 0 && (
          <ChartCard
            eyebrow="Tags manuales"
            title="Tags aplicados manualmente por ejecutivos"
            description="Etiquetas que solo los ejecutivos pueden aplicar a una conversación."
          >
            <HorizontalBarBreakdown data={tagsChartData} />
          </ChartCard>
        )}

        {businessUnitId === INSUREX_BUSINESS_UNIT_ID && salesByTypeChartData.length > 0 && (
          <ChartCard
            eyebrow="Sales por tipo"
            title="Qué seguros se están vendiendo"
            description="Distribución de sales por tipo de seguro: desgravamen y cesantía."
          >
            <HorizontalBarBreakdown data={salesByTypeChartData} />
          </ChartCard>
        )}

        {businessUnitId === INSUREX_BUSINESS_UNIT_ID && salesByInstitutionChartData.length > 0 && (
          <ChartCard
            eyebrow="Sales por institución"
            title="A qué instituciones financieras corresponden las sales"
            description="Distribución de sales por la institución registrada en el dato del crédito."
          >
            <HorizontalBarBreakdown data={salesByInstitutionChartData} />
          </ChartCard>
        )}

        {businessUnitId === KOVACS_BUSINESS_UNIT_ID && kovacsAICommentChartData.length > 0 && (
          <>
            <ChartCard
              eyebrow="Tipificación IA"
              title="Cómo la IA está clasificando las conversaciones"
              description="Categorías asignadas por la IA: Oportunidad, No Contesta, Número Inválido, etc."
            >
              <HorizontalBarBreakdown data={kovacsAICommentChartData} max={15} />
            </ChartCard>

            <ChartCard
              title="Leads por tipificación"
              description="Cantidad de leads por categoría. Haz clic en Ver leads para filtrar en la vista de leads."
            >
              <Box sx={{ pt: 2, pb: 2, px: 0 }}>
                <Grid container spacing={2.5}>
                  {Object.entries(leadsByAIComment)
                    .sort(([, a], [, b]) => b - a)
                    .map(([tipificacionLabel, count]) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={tipificacionLabel}>
                        <HardMetricCard
                          type={tipificacionLabel}
                          data={count}
                          tooltip={tipificacionLabel}
                          onActionButtonClick={
                            onNavigateToLeads && filters
                              ? () => {
                                  const url = buildLeadsFilterUrl({
                                    ...filters,
                                    AIComment: tipificacionLabel,
                                    viewMode: 'table',
                                  });
                                  onNavigateToLeads(url);
                                }
                              : undefined
                          }
                          navigationTooltip={`Ver leads con tipificación "${tipificacionLabel}"`}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </ChartCard>
          </>
        )}

        <ChartCard
          eyebrow="Tiempo de respuesta"
          title="¿Cuán rápido responden los leads a una plantilla?"
          description={
            totalTemplateMessages > 0
              ? `${totalTemplateMessages.toLocaleString()} mensajes enviados en el período.`
              : undefined
          }
        >
          {responseTimesMetrics ? (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showNoResponse}
                      onChange={(e) => setShowNoResponse(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Mostrar sin respuesta"
                />
                {FAILED_TEMPLATE_METRICS_ENABLED && (
                  <FormControlLabel
                    control={
                      <Switch checked={showFailed} onChange={(e) => setShowFailed(e.target.checked)} size="small" />
                    }
                    label="Mostrar Fallidos"
                  />
                )}
                {Object.keys(clientsByResponseTimeByTemplate).length > 0 && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showByTemplate}
                        onChange={(e) => setShowByTemplate(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Ver por plantilla"
                  />
                )}
              </Box>
              {!showByTemplate ? (
                <BarChart
                  colors={[alpha(NAVY, 0.65), NO_RESPONSE_BAR_COLOR, FAILED_BAR_COLOR]}
                  series={responseTimeChart.series}
                  height={290}
                  xAxis={[
                    {
                      data: responseTimeChart.keys.map(minutesToString),
                      scaleType: 'band',
                      height: 'auto' as const,
                    },
                  ]}
                  yAxis={[{ min: 0, tickMinStep: 1 }]}
                  margin={{ top: 16, bottom: 30, left: 32, right: 10 }}
                  grid={{ horizontal: true }}
                  hideLegend
                  sx={{
                    [`.${axisClasses.left} .${axisClasses.label}`]: { transform: 'translate(-20px, 0)' },
                    [`& .MuiChartsAxis-line, & .MuiChartsAxis-tick`]: { stroke: alpha(NAVY, 0.1) },
                    [`& .MuiChartsGrid-line`]: { stroke: alpha(NAVY, 0.06) },
                    [`& .MuiChartsAxis-tickLabel`]: { fill: alpha(NAVY, 0.6), fontFamily: 'Jost, sans-serif' },
                  }}
                  slots={{ tooltip: CategoryTooltip }}
                />
              ) : (
                <Box>
                  {Object.entries(clientsByResponseTimeByTemplate)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([templateName, templateData]) => {
                      const templateDataCopy: Record<number | string, number> = { ...templateData };
                      delete templateDataCopy[0];
                      if (!FAILED_TEMPLATE_METRICS_ENABLED) {
                        delete templateDataCopy.failed;
                      }
                      const totalForTemplate = Object.values(templateDataCopy).reduce(
                        (acc: number, val: number) => acc + val,
                        0
                      );

                      const templateDetails = clientsByResponseTimeByTemplateDetails[templateName];
                      const chart = buildResponseTimeChartSeries(templateDataCopy, showNoResponse, effectiveShowFailed);

                      return (
                        <Box
                          key={templateName}
                          sx={{
                            mb: 2,
                            p: { xs: 2, md: 2.5 },
                            backgroundColor: SURFACE_CONTAINER_LOW,
                            borderRadius: RADIUS_CARD,
                          }}
                        >
                          <Box
                            sx={{
                              mb: 1.5,
                              display: 'flex',
                              alignItems: { xs: 'flex-start', md: 'center' },
                              justifyContent: 'space-between',
                              gap: 2,
                              flexDirection: { xs: 'column', md: 'row' },
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontFamily: 'Jost, sans-serif',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  letterSpacing: '0.06em',
                                  textTransform: 'uppercase',
                                  color: alpha(NAVY, 0.6),
                                }}
                              >
                                Plantilla
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'Jost, sans-serif',
                                  fontWeight: 700,
                                  fontSize: '1rem',
                                  color: NAVY,
                                }}
                              >
                                {templateName}{' '}
                                <Typography
                                  component="span"
                                  sx={{ color: alpha(NAVY, 0.5), fontWeight: 500, fontSize: '0.875rem' }}
                                >
                                  · {totalForTemplate.toLocaleString()} mensajes
                                </Typography>
                              </Typography>
                            </Box>
                            {templateDetails && (
                              <Button
                                size="small"
                                startIcon={<ManageSearchIcon />}
                                onClick={() => handleOpenTemplateClientsDialog(templateName)}
                                sx={{
                                  fontFamily: 'Jost, sans-serif',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: RADIUS_COIN,
                                  backgroundColor: alpha(YELLOW, 0.18),
                                  color: NAVY,
                                  px: 2,
                                  '&:hover': { backgroundColor: alpha(YELLOW, 0.32) },
                                }}
                              >
                                Ver leads
                              </Button>
                            )}
                          </Box>
                          <BarChart
                            colors={[alpha(NAVY, 0.65), NO_RESPONSE_BAR_COLOR, FAILED_BAR_COLOR]}
                            series={chart.series}
                            height={240}
                            xAxis={[
                              {
                                data: chart.keys.map(minutesToString),
                                scaleType: 'band',
                                height: 'auto' as const,
                              },
                            ]}
                            yAxis={[{ min: 0, tickMinStep: 1 }]}
                            margin={{ top: 12, bottom: 30, left: 32, right: 10 }}
                            grid={{ horizontal: true }}
                            hideLegend
                            sx={{
                              [`.${axisClasses.left} .${axisClasses.label}`]: { transform: 'translate(-20px, 0)' },
                              [`& .MuiChartsAxis-line, & .MuiChartsAxis-tick`]: { stroke: alpha(NAVY, 0.1) },
                              [`& .MuiChartsGrid-line`]: { stroke: alpha(NAVY, 0.06) },
                              [`& .MuiChartsAxis-tickLabel`]: {
                                fill: alpha(NAVY, 0.6),
                                fontFamily: 'Jost, sans-serif',
                              },
                            }}
                            slots={{ tooltip: CategoryTooltip }}
                          />
                        </Box>
                      );
                    })}
                </Box>
              )}
            </>
          ) : (
            <CircularProgress />
          )}
        </ChartCard>
      </Grid>
      <Dialog
        open={!!templateClientsDialog}
        onClose={handleCloseTemplateClientsDialog}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: {
            sx: {
              borderRadius: RADIUS_COIN,
              boxShadow: SHADOW,
              overflow: 'hidden',
              backgroundColor: GLASS_BACKGROUND,
              backdropFilter: GLASS_BLUR,
              WebkitBackdropFilter: GLASS_BLUR,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: NAVY,
            color: SURFACE_CONTAINER_LOWEST,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon />
            <Box>
              <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '1.15rem' }}>
                Leads por plantilla
              </Typography>
              <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', opacity: 0.75 }}>
                {templateClientsDialog?.templateName}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: 'transparent' }}>
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                pb: 1,
                borderBottom: '2px solid',
                borderColor: 'success.main',
              }}
            >
              <Chip
                label={`${templateClientsDialog?.responded.length || 0} leads`}
                color="success"
                size="small"
                variant="outlined"
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                Respondieron
              </Typography>
            </Box>
            {templateClientsDialog && renderClientList(templateClientsDialog.responded)}
          </Box>
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                pb: 1,
                borderBottom: '2px solid',
                borderColor: NO_RESPONSE_BAR_COLOR,
              }}
            >
              <Chip
                label={`${templateClientsDialog?.noResponse.length || 0} leads`}
                size="small"
                variant="outlined"
                sx={{ borderColor: NO_RESPONSE_BAR_COLOR, color: NO_RESPONSE_BAR_COLOR }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: NO_RESPONSE_BAR_COLOR }}>
                Sin respuesta
              </Typography>
            </Box>
            {templateClientsDialog && renderClientList(templateClientsDialog.noResponse)}
          </Box>
          {FAILED_TEMPLATE_METRICS_ENABLED && (
            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  pb: 1,
                  borderBottom: '2px solid',
                  borderColor: 'error.main',
                }}
              >
                <Chip
                  label={`${templateClientsDialog?.failed?.length ?? 0} leads`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                  Fallidos
                </Typography>
              </Box>
              {templateClientsDialog && renderClientList(templateClientsDialog.failed ?? [], { allowResend: true })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={handleDownloadCSV}
            startIcon={<DownloadIcon />}
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: RADIUS_COIN,
              backgroundColor: alpha(NAVY, 0.06),
              color: NAVY,
              px: 2,
              '&:hover': { backgroundColor: alpha(NAVY, 0.12) },
            }}
          >
            Descargar CSV
          </Button>
          <Button
            onClick={handleCloseTemplateClientsDialog}
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: RADIUS_COIN,
              backgroundColor: YELLOW,
              color: NAVY,
              px: 3,
              '&:hover': { backgroundColor: '#e5bf00' },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      {resendClientTarget && resendClient && (
        <TemplateMessageModal
          client={resendClient}
          businessUnitId={resendClient.businessUnitId}
          open
          setOpen={(open) => {
            if (!open) {
              handleCloseResendTemplateModal();
            }
          }}
          refetch={() => {}}
          allowTemplateCreation={false}
          initialTemplateName={resendClientTarget.templateName}
          pausesAiOnSend={false}
        />
      )}
    </Box>
  );
};

export default LeadCharts;
